import { useState } from 'react';
import { Menu, X, Copy, Check, AlertCircle, Terminal, Eye, Power } from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';
import { FooterSection } from '../components/sections';

/**
 * Quick Start Documentation - Blueprint Compliant
 *
 * Must start with:
 * - Helm install
 * - One annotation
 * - One verification command
 * - One metric to watch
 *
 * Must include early:
 * - How to disable
 * - How to uninstall
 * - How to inspect decisions
 *
 * Docs are a trust surface, not just instructions.
 */
export const QuickStart = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedCommand(id);
        setTimeout(() => setCopiedCommand(null), 2000);
    };

    return (
        <div className="min-h-screen bg-obsidian-900 text-white antialiased">
            {/* Navbar */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <ForecasterLogo />
                    <div className="hidden md:flex items-center gap-6">
                        <a href="/" className="text-gray-400 hover:text-white transition-colors">
                            Home
                        </a>
                        <a href="/docs" className="text-white font-medium">
                            Docs
                        </a>
                    </div>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden p-2 rounded-lg hover:bg-white/10"
                    >
                        {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-white/[0.06] bg-obsidian-900 px-6 py-4 space-y-3">
                        <a href="/" className="block text-gray-400 hover:text-white transition-colors">
                            Home
                        </a>
                        <a href="/docs" className="block text-white font-medium">
                            Docs
                        </a>
                    </div>
                )}
            </nav>

            {/* Content */}
            <div className="pt-24 pb-20">
                <div className="max-w-4xl mx-auto px-6">
                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="text-4xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
                            Quick start
                        </h1>
                        <p className="text-xl text-gray-400 leading-relaxed">
                            Install the controller, annotate one workload, and watch GPU efficiency improve.
                        </p>
                    </div>

                    {/* Prerequisites */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
                            <AlertCircle className="w-6 h-6 text-blue-400" />
                            Prerequisites
                        </h2>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                            <ul className="space-y-2 text-gray-300">
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>Kubernetes 1.24+</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>Helm 3.x</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>kubectl configured and working</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-400 mt-1">•</span>
                                    <span>At least one inference workload (vLLM, SGLang, TensorRT-LLM)</span>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Step 1: Install */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
                            <Terminal className="w-6 h-6 text-emerald-400" />
                            Step 1: Install the controller
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Install Forecaster using Helm. This deploys the controller into the <code className="px-2 py-1 bg-black/40 rounded text-sm">forecaster-system</code> namespace.
                        </p>

                        <CodeBlock
                            id="install"
                            code={`# Add the Forecaster Helm repository
helm repo add forecaster https://forecaster.ai/charts
helm repo update

# Install the controller
helm install forecaster forecaster/forecaster \\
  --namespace forecaster-system \\
  --create-namespace

# Verify installation
kubectl get pods -n forecaster-system`}
                            onCopy={copyToClipboard}
                            isCopied={copiedCommand === 'install'}
                        />

                        <div className="mt-4 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                            <p className="text-sm text-gray-300">
                                <strong className="text-emerald-400">Expected output:</strong> You should see the <code className="px-1.5 py-0.5 bg-black/40 rounded text-xs">forecaster-controller</code> pod in Running status.
                            </p>
                        </div>
                    </section>

                    {/* Step 2: Annotate */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
                            <Terminal className="w-6 h-6 text-blue-400" />
                            Step 2: Annotate your workload
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Add the <code className="px-2 py-1 bg-black/40 rounded text-sm">forecaster.ai/optimize</code> annotation to opt in. Nothing changes until you do this.
                        </p>

                        <CodeBlock
                            id="annotate"
                            code={`# Option 1: Annotate existing deployment
kubectl annotate deployment my-inference-service \\
  forecaster.ai/optimize=true

# Option 2: Add to your YAML manifest
apiVersion: apps/v1
kind: Deployment
metadata:
  name: my-inference-service
  annotations:
    forecaster.ai/optimize: "true"
spec:
  # ... rest of your deployment`}
                            onCopy={copyToClipboard}
                            isCopied={copiedCommand === 'annotate'}
                        />

                        <div className="mt-4 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                            <p className="text-sm text-gray-300">
                                <strong className="text-blue-400">What happens:</strong> Forecaster starts monitoring this workload's metrics and will begin making optimization decisions within 5-10 minutes.
                            </p>
                        </div>
                    </section>

                    {/* Step 3: Verify */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
                            <Eye className="w-6 h-6 text-purple-400" />
                            Step 3: Verify it's working
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Watch Kubernetes events to see Forecaster's decisions in real-time.
                        </p>

                        <CodeBlock
                            id="verify"
                            code={`# Watch events for your workload
kubectl get events \\
  --field-selector involvedObject.name=my-inference-service \\
  --watch

# Check Forecaster controller logs
kubectl logs -n forecaster-system \\
  -l app=forecaster-controller \\
  --tail=50 --follow`}
                            onCopy={copyToClipboard}
                            isCopied={copiedCommand === 'verify'}
                        />

                        <div className="mt-4 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                            <p className="text-sm text-gray-300 mb-3">
                                <strong className="text-purple-400">What to look for:</strong>
                            </p>
                            <ul className="space-y-1 text-sm text-gray-400">
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-400 mt-0.5">•</span>
                                    <span>Events like "Scaled deployment to 4 replicas (predicted load increase)"</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-400 mt-0.5">•</span>
                                    <span>"Routing traffic to replica-2 (KV cache affinity)"</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-purple-400 mt-0.5">•</span>
                                    <span>"Cost reduced from $2.40 to $0.95 per 1K requests"</span>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Step 4: Monitor */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4">
                            Step 4: Monitor the results
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Check metrics to verify cost reduction and latency stability.
                        </p>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                                <h3 className="text-white font-medium mb-2">Cost per 1K requests</h3>
                                <p className="text-3xl font-bold text-emerald-400 mb-2">$0.95</p>
                                <p className="text-sm text-gray-400">Down from $2.40 (60% reduction)</p>
                            </div>
                            <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                                <h3 className="text-white font-medium mb-2">P95 latency</h3>
                                <p className="text-3xl font-bold text-blue-400 mb-2">Low</p>
                                <p className="text-sm text-gray-400">Stable latency, even during traffic spikes</p>
                            </div>
                        </div>

                        <div className="mt-4">
                            <CodeBlock
                                id="metrics"
                                code={`# Query Prometheus metrics (if installed)
# Cost efficiency
forecaster_cost_per_1k_requests{deployment="my-inference-service"}

# Latency
forecaster_p95_latency_ms{deployment="my-inference-service"}`}
                                onCopy={copyToClipboard}
                                isCopied={copiedCommand === 'metrics'}
                            />
                        </div>
                    </section>

                    {/* Important: How to disable */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
                            <Power className="w-6 h-6 text-orange-400" />
                            How to disable (if needed)
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Remove the annotation to stop optimization. Your workload continues running unchanged.
                        </p>

                        <CodeBlock
                            id="disable"
                            code={`# Remove annotation to disable optimization
kubectl annotate deployment my-inference-service \\
  forecaster.ai/optimize-

# Forecaster stops managing this workload immediately`}
                            onCopy={copyToClipboard}
                            isCopied={copiedCommand === 'disable'}
                        />

                        <div className="mt-4 p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
                            <p className="text-sm text-gray-300">
                                <strong className="text-orange-400">Safe to disable:</strong> Forecaster only adjusts replica counts and routing. Disabling it leaves your workload in its current state—no rollback needed.
                            </p>
                        </div>
                    </section>

                    {/* Uninstall */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4">
                            How to uninstall
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Remove Forecaster completely from your cluster.
                        </p>

                        <CodeBlock
                            id="uninstall"
                            code={`# 1. Remove annotations from all workloads
kubectl annotate deployment --all \\
  forecaster.ai/optimize- \\
  --all-namespaces

# 2. Uninstall the controller
helm uninstall forecaster --namespace forecaster-system

# 3. Delete the namespace (optional)
kubectl delete namespace forecaster-system`}
                            onCopy={copyToClipboard}
                            isCopied={copiedCommand === 'uninstall'}
                        />
                    </section>

                    {/* Next steps */}
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold text-white mb-4">
                            Next steps
                        </h2>
                        <div className="space-y-4">
                            <a
                                href="/docs/configuration"
                                className="block p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/20 hover:bg-blue-500/5 transition-all group"
                            >
                                <h3 className="text-white font-medium mb-1 group-hover:text-blue-400 transition-colors">
                                    Graduate to CRDs
                                </h3>
                                <p className="text-sm text-gray-400">
                                    Learn how to use Custom Resources for fine-grained control and GitOps workflows.
                                </p>
                            </a>
                            <a
                                href="/docs/how-it-works"
                                className="block p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-purple-500/20 hover:bg-purple-500/5 transition-all group"
                            >
                                <h3 className="text-white font-medium mb-1 group-hover:text-purple-400 transition-colors">
                                    Understand the decision model
                                </h3>
                                <p className="text-sm text-gray-400">
                                    Deep dive into how Forecaster makes optimization decisions.
                                </p>
                            </a>
                            <a
                                href="/docs/troubleshooting"
                                className="block p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-emerald-500/20 hover:bg-emerald-500/5 transition-all group"
                            >
                                <h3 className="text-white font-medium mb-1 group-hover:text-emerald-400 transition-colors">
                                    Troubleshooting
                                </h3>
                                <p className="text-sm text-gray-400">
                                    Common issues and how to resolve them.
                                </p>
                            </a>
                        </div>
                    </section>

                    {/* Support */}
                    <section>
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                            <h3 className="text-white font-medium mb-2">Need help?</h3>
                            <p className="text-gray-400 mb-4">
                                Reach out to us or check the community forum.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    href="mailto:forecaster.k8s@gmail.com"
                                    className="px-6 py-2.5 bg-volt-500 text-obsidian-900 rounded-lg font-medium hover:bg-volt-400 transition-colors"
                                >
                                    Email support
                                </a>
                                <a
                                    href="https://github.com/forecaster-ai/forecaster/issues"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-6 py-2.5 border border-white/20 rounded-lg font-medium text-white hover:bg-white/5 transition-colors"
                                >
                                    GitHub Issues
                                </a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Footer */}
            <FooterSection />
        </div>
    );
};

// Code block component with copy functionality
const CodeBlock = ({
    id,
    code,
    onCopy,
    isCopied,
}: {
    id: string;
    code: string;
    onCopy: (text: string, id: string) => void;
    isCopied: boolean;
}) => {
    return (
        <div className="relative group">
            <pre className="bg-black/60 border border-white/10 rounded-lg p-4 text-sm font-mono text-gray-300 overflow-x-auto">
                {code}
            </pre>
            <button
                onClick={() => onCopy(code, id)}
                className="absolute top-3 right-3 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all opacity-0 group-hover:opacity-100"
                title="Copy to clipboard"
            >
                {isCopied ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                )}
            </button>
        </div>
    );
};
